import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Plus, Trash2, Search, Maximize2 } from 'lucide-react';
import { useAIChatStore } from '../../stores/aiChatStore';
import { useCurrentView } from '../../hooks/useCurrentView';
import { ChatMessage } from './ChatMessage';
import { SuggestionPreview } from './SuggestionPreview';
import { AiCostChip } from '../AiCostChip';

interface ChatPanelProps {
  onClose: () => void;
}

export function ChatPanel({ onClose }: ChatPanelProps) {
  const {
    conversations,
    currentConversationId,
    isLoading,
    dailyUsage,
    lastReplyMock,
    createConversation,
    deleteConversation,
    setCurrentConversation,
    sendMessage,
    markSuggestionApplied
  } = useAIChatStore();

  const remaining = dailyUsage ? Math.max(0, dailyUsage.limit - dailyUsage.used) : null;

  const [input, setInput] = useState('');
  const [showSuggestion, setShowSuggestion] = useState<any>(null);
  const [showSearch, setShowSearch] = useState(false);
  const currentView = useCurrentView((s) => s.currentView);
  const setCurrentView = useCurrentView((s) => s.setCurrentView);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleExpand = () => {
    onClose();
    setCurrentView('assistant');
  };

  const currentConversation = conversations.find(c => c.id === currentConversationId);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [currentConversation?.messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const message = input;
    setInput('');

    if (!currentConversationId) {
      createConversation();
    }

    await sendMessage(message);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewChat = () => {
    createConversation();
  };

  // ⚠️ The messageId rides along so the applied stamp can land on the message
  // it came from — without it the Apply button returns and charges again.
  const handleApplySuggestion = (suggestion: any, messageId: string) => {
    setShowSuggestion({ suggestion, messageId });
  };

  return (
    <>
      <div className="fixed bottom-24 right-6 w-96 h-[600px] bg-card rounded-2xl shadow-2xl
        border border-border flex flex-col z-40 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-border bg-card/50 backdrop-blur">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">AI Assistant</h3>
            {lastReplyMock && (
              <span
                className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-full border border-amber-500/20"
                title="The daily AI limit was reached or the service is unavailable — replies are offline demos."
              >
                Offline
              </span>
            )}
            {remaining !== null && (
              <span
                className="text-xs text-muted-foreground"
                title={`${dailyUsage!.used} of ${dailyUsage!.limit} AI requests used today`}
              >
                {remaining} left today
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSearch((v) => !v)}
              aria-pressed={showSearch}
              className={`p-2 rounded-lg transition-colors ${showSearch ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground'}`}
              title="Search (coming soon)"
            >
              <Search className="w-4 h-4" />
            </button>
            <button
              onClick={handleExpand}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="Open full view"
            >
              <Maximize2 className="w-4 h-4 text-muted-foreground" />
            </button>
            <button
              onClick={handleNewChat}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
              title="New conversation"
            >
              <Plus className="w-4 h-4 text-muted-foreground" />
            </button>
            {currentConversationId && (
              <button
                onClick={() => deleteConversation(currentConversationId)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                title="Delete conversation"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
        </div>

        {/* Universal search — placeholder entry point (#14). No engine yet; this
            stub establishes the location + the page-aware direction. */}
        {showSearch && (
          <div className="border-b border-border bg-muted/30 px-4 py-3 space-y-2">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                disabled
                placeholder="Search everything… (coming soon)"
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none disabled:cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Page-aware universal search is on the way — it'll scope to{' '}
              <span className="font-medium text-foreground capitalize">{currentView}</span> with a removable filter.
            </p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!currentConversation || currentConversation.messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-2 max-w-sm">
                <p className="text-muted-foreground text-sm">
                  Hi! I'm your METANOIA assistant. I know your domains, goals, and what you're working on —
                  ask me to plan a routine, build a project, design your day, or think through a goal.
                </p>
                <div className="space-y-2 pt-4">
                  <button
                    onClick={() => setInput("Help me create a morning routine")}
                    className="w-full px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-lg
                      text-foreground transition-colors"
                  >
                    Create a morning routine
                  </button>
                  <button
                    onClick={() => setInput("Build a project for learning a new skill")}
                    className="w-full px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-lg
                      text-foreground transition-colors"
                  >
                    Build a learning project
                  </button>
                  <button
                    onClick={() => setInput("Design a complete day template")}
                    className="w-full px-4 py-2 text-sm bg-muted hover:bg-muted/80 rounded-lg
                      text-foreground transition-colors"
                  >
                    Design a day template
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {currentConversation.messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onApplySuggestion={handleApplySuggestion}
                />
              ))}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-2">
                    <p className="text-sm text-muted-foreground">Thinking...</p>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="p-4 border-t border-border bg-card/50 backdrop-blur">
          <div className="flex gap-2 items-center">
            <AiCostChip action="chat_message" />
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-background border border-border rounded-lg
                text-foreground placeholder:text-muted-foreground
                focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg
                hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showSuggestion && (
        <SuggestionPreview
          suggestion={showSuggestion.suggestion}
          onClose={() => setShowSuggestion(null)}
          onApply={() => {
            markSuggestionApplied(showSuggestion.messageId);
            setShowSuggestion(null);
          }}
        />
      )}
    </>
  );
}
